import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  getAvailableCompaniesAsync,
  CompanyResponse,
} from '@/app/shared/api/endpoints/companies';
import {
  readSavedCompanyId,
  saveCompanyId,
  clearCompanyId,
} from '@/app/shared/utils/sessionStorage';
import { queryClient } from '@/app/shared/api/queryClient';
import { useDebtorAccount } from '@/app/shared/utils/hooks/useDebtorAccount';
import { configureOktaInstanceAsync } from '@/app/shared/api/endpoints/auth';
import { CompanyContextType } from './CompanyContext.types';
import { apiInstance } from '../api/apiInstance';


export const CompanyContext = createContext<CompanyContextType>({
    selectedCompanyId : null,
    selectedCompany : null,
    availableCompanies : [],
    companies : [],
    isLoading : false,
    error : null,
    selectCompany: async() => {},
    initialized : false
});

export const CompanyProvider: React.FC<{ children: React.ReactNode }> = ({children,}) => {

    const {selectedDebtor, initialized:debtorInitialized} = useDebtorAccount();
    const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(
    () => readSavedCompanyId()
    );
    const [companies,setCompanies] = useState<CompanyResponse[]>([]);
    const [isLoading,setIsloading] = useState<boolean>(false);
    const [error,setError] = useState<string | null>(null);
    const [initialized,setInitialized] = useState<boolean>(false);
    const prevDebtorIdRef = useRef<number | undefined>(undefined);

    /**
   * Reconfigure API instance when company changes
   * This ensures the companyid header is updated
   */
    const updateCompanyHeader = useCallback(async (companyId: number) => {
         if (apiInstance.defaults.headers.common) {
      apiInstance.defaults.headers.common.companyid = companyId;
    }
  }, []);

    useEffect(()=>{
        if(!debtorInitialized || !selectedDebtor || initialized) return;

        const fetchCompanies = async()=> {
            try{
                const fetchedCompanies = await getAvailableCompaniesAsync();
                setCompanies(fetchedCompanies);

                const availableIds = fetchedCompanies.map((c) => c.companyId);
                const savedCompanyId = readSavedCompanyId();
                let companyToSelect: number;

                if(savedCompanyId && availableIds.includes(savedCompanyId)){
                    // Restore saved company if it's in the available list
                    companyToSelect = savedCompanyId;
                    setSelectedCompanyId(savedCompanyId);
                } else if (availableIds.length > 0) {
                    // Auto-select first company from the list
                    companyToSelect = availableIds[0];
                    setSelectedCompanyId(availableIds[0]);
                    saveCompanyId(availableIds[0]);
                } else {
                    // Fallback to company ID 1 if no companies returned
                    companyToSelect = 1;
                    setSelectedCompanyId(1);
                    saveCompanyId(1);
                }

                // Update API instance headers with selected company
                await updateCompanyHeader(companyToSelect);

                setInitialized(true);
            }catch(err){
                setError('Failed to load available companies.');
                console.error('Error fetching companies:', err);
            }finally{
                setIsloading(false);
            }
        };
        fetchCompanies();
    } , [debtorInitialized, selectedDebtor, initialized, updateCompanyHeader]);

    // Derive the id list and the selected company's full detail from the
    // backend-provided company objects.
    const availableCompanies = useMemo(
        () => companies.map((c) => c.companyId),
        [companies]
    );

    const selectedCompany = useMemo(
        () => companies.find((c) => c.companyId === selectedCompanyId) ?? null,
        [companies, selectedCompanyId]
    );

    const selectCompany = useCallback(async(companyId: number)=>{
        if(companyId === selectedCompanyId) return;

        if(!availableCompanies.includes(companyId)){
            setError(`Company id ${companyId} is not available in the list`);
            return;
        }

        setError(null);

        try{
            saveCompanyId(companyId)
            setSelectedCompanyId(companyId);
            await updateCompanyHeader(companyId);
            queryClient.invalidateQueries();
        }catch(err){
            setError("failed to switch companyies");
            console.error('Error switching companies:', err);
        }
    },[selectedCompanyId,availableCompanies,updateCompanyHeader]);
    
    /**
   * Reset company state when debtor changes from one defined ID to another
   * This runs when:
   * - Debtor switches from A to B (defined → different defined)
   * - Debtor logs out (defined → undefined)
   *
   * DOES NOT run when:
   * - Initial mount/page refresh (undefined → defined)
   */
    useEffect(() => {
        const currentDebtorId = selectedDebtor?.debtorId;
        const prevDebtorId = prevDebtorIdRef.current;

        // Only clear when going from one defined debtor to another (or to undefined)
        if (prevDebtorId !== undefined && prevDebtorId !== currentDebtorId) {
            clearCompanyId();
            setSelectedCompanyId(null);
            setCompanies([]);
            setInitialized(false);
            setError(null);
        }

        // Update ref for next comparison
        prevDebtorIdRef.current = currentDebtorId;
    }, [selectedDebtor?.debtorId]);

    const value = useMemo(
    () => ({
      selectedCompanyId,
      selectedCompany,
      availableCompanies,
      companies,
      isLoading,
      error,
      selectCompany,
      initialized,
    }),
    [
      selectedCompanyId,
      selectedCompany,
      availableCompanies,
      companies,
      isLoading,
      error,
      selectCompany,
      initialized,
    ]
  );

  return (
    <CompanyContext.Provider value={value}>
      {children}
    </CompanyContext.Provider>
  );
};